import React from 'react';
import { cn } from '@rocco/utils/cn';

interface DirectoryProductGridSkeletonProps {
    /**
     * Layout variant to display:
     * - 'A': Featured tile on the RIGHT
     * - 'B': Featured tile on the LEFT
     * Only used when useFeaturedLayout is true.
     */
    layout?: 'A' | 'B';
    /**
     * Optional class name for additional styling
     */
    className?: string;
    /**
     * When true, renders featured layout (tall tile + 8 top + 5 bottom = 13 products + 1 ad).
     * When false, renders flat grid of 15 square tiles (3 rows of 5).
     * Defaults to false (flat grid) to match the common no-ad case.
     */
    useFeaturedLayout?: boolean;
}

/**
 * Skeleton loader matching DirectoryGridSection layout.
 * Adapts to two modes:
 * - Featured layout: 1 tall featured tile + 8 top grid + 5 bottom row (when ads present)
 * - Flat grid: 15 square tiles in 3 rows of 5 (when no ads)
 * Includes fade-in animation to smooth the skeleton → content transition.
 */
export const DirectoryProductGridSkeleton = ({
    layout = 'A',
    className,
    useFeaturedLayout = false,
}: DirectoryProductGridSkeletonProps) => {
    // Flat grid: 15 square tiles in 3 rows of 5
    if (!useFeaturedLayout) {
        return (
            <div
                role="status"
                aria-label="Loading products"
                className={cn('flex flex-col', className)}
            >
                <span className="sr-only">
                    Loading products, please wait...
                </span>
                <div
                    className={cn(
                        'grid gap-x-5 gap-y-20',
                        'md:grid-cols-4 lg:grid-cols-5',
                    )}
                >
                    {Array.from({ length: 15 }, (_, i) => (
                        <ProductTileSkeleton key={`flat-${i}`} />
                    ))}
                </div>
            </div>
        );
    }

    // Featured layout: 1 tall tile + 8 top grid + 5 bottom row
    return (
        <div
            role="status"
            aria-label="Loading products"
            className={cn('flex flex-col', className)}
        >
            <span className="sr-only">Loading products, please wait...</span>

            {/* Top grid: 1 featured + 8 product tiles */}
            <div
                className={cn(
                    'grid gap-x-5 gap-y-20',
                    'md:grid-cols-4 lg:grid-cols-5',
                )}
            >
                {layout === 'A' ? (
                    <>
                        {/* Row 1: 4 product skeletons */}
                        {[1, 2, 3, 4].map((i) => (
                            <ProductTileSkeleton key={`r1-${i}`} />
                        ))}

                        {/* Featured skeleton spanning 2 rows - on the right */}
                        <div className="row-span-2">
                            <FeaturedTileSkeleton />
                        </div>

                        {/* Row 2: 4 product skeletons */}
                        {[5, 6, 7, 8].map((i) => (
                            <ProductTileSkeleton key={`r2-${i}`} />
                        ))}
                    </>
                ) : (
                    <>
                        {/* Featured skeleton spanning 2 rows - on the left */}
                        <div className="row-span-2">
                            <FeaturedTileSkeleton />
                        </div>

                        {/* Row 1: 4 product skeletons */}
                        {[1, 2, 3, 4].map((i) => (
                            <ProductTileSkeleton key={`r1-${i}`} />
                        ))}

                        {/* Row 2: 4 product skeletons */}
                        {[5, 6, 7, 8].map((i) => (
                            <ProductTileSkeleton key={`r2-${i}`} />
                        ))}
                    </>
                )}
            </div>

            {/* Bottom row: 5 product tiles */}
            <div
                className={cn(
                    'mt-20 grid gap-x-5 gap-y-20',
                    'md:grid-cols-4 lg:grid-cols-5',
                )}
            >
                {[1, 2, 3, 4, 5].map((i) => (
                    <ProductTileSkeleton key={`bottom-${i}`} />
                ))}
            </div>
        </div>
    );
};

/**
 * Square product tile skeleton matching DirectoryProductTile structure.
 * Includes image placeholder and text placeholders.
 */
const ProductTileSkeleton = () => (
    <div className="gap-3.75 flex flex-col">
        {/* Image placeholder - aspect-square to match DirectoryProductTile */}
        <div className="aspect-square w-full animate-pulse rounded-lg bg-gray-200" />

        {/* Text placeholders */}
        <div className="flex flex-col gap-0.5">
            {/* Title placeholder - matches text-lg leading-[1.3]: 1.25rem × 1.3 = 1.625rem */}
            <div className="h-[1.625rem] w-3/4 animate-pulse rounded bg-gray-200" />
            {/* Professional name placeholder - matches text-base leading-[1.3]: 1rem × 1.3 = 1.3rem */}
            <div className="h-[1.3rem] w-1/2 animate-pulse rounded bg-gray-200" />
        </div>
    </div>
);

/**
 * Tall featured tile skeleton matching DirectoryFeaturedTile structure.
 * Uses padding-bottom to create intrinsic height matching 2 product rows + gap.
 */
const FeaturedTileSkeleton = () => (
    <div className="gap-3.75 flex flex-col">
        {/* Image placeholder - padding-bottom creates height = 2 × width + 9rem (matching DirectoryFeaturedTile) */}
        <div className="relative w-full overflow-hidden rounded-lg bg-gray-200">
            <div
                className="animate-pulse"
                style={{ paddingBottom: 'calc(200% + 9rem)' }}
            />
        </div>

        {/* Text placeholders */}
        <div className="flex shrink-0 flex-col gap-0.5">
            {/* Title placeholder - matches text-lg leading-[1.3]: 1.25rem × 1.3 = 1.625rem */}
            <div className="h-[1.625rem] w-3/4 animate-pulse rounded bg-gray-200" />
            {/* Professional name placeholder - matches text-base leading-[1.3]: 1rem × 1.3 = 1.3rem */}
            <div className="h-[1.3rem] w-1/2 animate-pulse rounded bg-gray-200" />
        </div>
    </div>
);

DirectoryProductGridSkeleton.displayName = 'DirectoryProductGridSkeleton';
